// geldEingabe.ts — Betragsfelder des Zahlungsverkehrs: Text ↔ Zahl.
//
// Eine Quelle für beide Masken (Positionen und Rechnungskopf). Vorher stand
// dieselbe Ableitung zweimal im Code — und ein Fehler darin hätte sich still
// auf die eine Hälfte beschränkt.

/**
 * Kaufmännisch auf Cent runden.
 *
 * `Math.round(betrag * 100) / 100` genügt hier NICHT: 1,005 wird binär als
 * 1.00499999999999989 dargestellt, `1.005 * 100` ergibt 100.49999999999999 und
 * `Math.round` daraus 100 — also 1,00 statt 1,01. Der Epsilon-Zuschlag hebt
 * genau diesen Darstellungsfehler auf.
 *
 * Das Vorzeichen wird separat geführt, damit auch Gutschriften symmetrisch
 * runden: `Math.round` rundet zu +unendlich, −1,005 ergäbe sonst −1,00.
 */
export function rundeAufCent(betrag: number): number {
  const vorzeichen = betrag < 0 ? -1 : 1;
  const roh = Math.abs(betrag);
  return (vorzeichen * Math.round((roh + Number.EPSILON) * 100)) / 100;
}

/**
 * Betragseingabe → Zahl. Versteht deutsche und englische Schreibweise:
 * „1.234,56" → 1234.56 · „1234.56" → 1234.56 · „-500" → -500 · leer/unlesbar → null.
 *
 * Der Punkt ist mehrdeutig — Tausendertrenner ODER Dezimaltrenner. Entschieden
 * wird am Komma: gibt es eins, ist es der Dezimaltrenner und alle Punkte sind
 * Tausendertrenner. Ohne Komma gilt ein einzelner Punkt mit ein bis zwei
 * Nachkommastellen als Dezimaltrenner (so tippt man „1234.56"); alles andere ist
 * Tausendergliederung. Blind alle Punkte zu entfernen machte aus „1234.56"
 * sonst 123456 — den hundertfachen Betrag.
 *
 * Negative Beträge sind zugelassen: Gutschriften sind im Bestand normal.
 */
export function parseBetrag(text: string): number | null {
  const roh = text.trim().replace(/\s/g, "");
  if (!roh) return null;
  if (!/^-?[\d.,]+$/.test(roh)) return null;

  let normiert: string;
  if (roh.includes(",")) {
    normiert = roh.replace(/\./g, "").replace(",", ".");
  } else {
    const punkte = roh.split(".").length - 1;
    const nachkomma = punkte === 1 ? roh.length - roh.lastIndexOf(".") - 1 : 0;
    normiert = punkte === 1 && nachkomma >= 1 && nachkomma <= 2 ? roh : roh.replace(/\./g, "");
  }

  const zahl = Number(normiert);
  return Number.isFinite(zahl) ? rundeAufCent(zahl) : null;
}

/**
 * Zahl → Eingabetext: ohne Tausenderpunkte, mit Komma — so tippt man Beträge.
 *
 * Der Wert kommt als `number`, kann aber aus einer TypeORM-`decimal`-Spalte auch
 * als String eintreffen, wenn irgendwo der Transformer fehlt. Deshalb wird hier
 * defensiv umgewandelt statt blind `.toFixed` aufzurufen — das würde auf einem
 * String werfen und die ganze Maske weissbrennen.
 */
export function formatBetragEingabe(wert: number | string | null | undefined): string {
  if (wert === null || wert === undefined || wert === "") return "";
  const zahl = typeof wert === "number" ? wert : Number(wert);
  if (!Number.isFinite(zahl)) return "";
  return zahl.toFixed(2).replace(".", ",");
}
